// Generated by GitHub Copilot
import React, { useState } from "react";
import PropTypes from "prop-types";
import { FaSearch, FaSort } from "react-icons/fa";
import UserListItem from "./UserListItem";
import ConfirmDialog from "../../common/ConfirmDialog";
import styles from "./UserList.module.css";
import { trackUserSearch, trackUserSortChange, trackUserDelete } from "../../../utils/modernUITracking";

/**
 * User List Component with search, filter, and management capabilities
 */
const UserList = ({ users, onUserUpdated, onUserDeleted, currentUser }) => {
  const [filteredUsers, setFilteredUsers] = useState(users);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("email");
  const [sortDirection, setSortDirection] = useState("asc");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // Update filtered users when users prop changes
  React.useEffect(() => {
    applyFiltersAndSort();
  }, [users, searchTerm, sortField, sortDirection]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Apply search filters and sorting
   */
  const applyFiltersAndSort = () => {
    let filtered = [...users];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.email.toLowerCase().includes(term) ||
          (user.name && user.name.toLowerCase().includes(term))
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortField] || "";
      let bValue = b[sortField] || "";

      // Special handling for privilege and feature counts
      if (sortField === "privileges") {
        aValue = Object.keys(a.privileges || {}).length;
        bValue = Object.keys(b.privileges || {}).length;
      } else if (sortField === "features") {
        aValue = Object.keys(a.features || {}).length;
        bValue = Object.keys(b.features || {}).length;
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      const result = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortDirection === "asc" ? result : -result;
    });

    setFilteredUsers(filtered);
  };

  /**
   * Handle search input change
   */
  const handleSearchChange = (event) => {
    const term = event.target.value;
    setSearchTerm(term);

    // Track search after a short delay to avoid tracking every keystroke
    if (term.length >= 2 || term.length === 0) {
      // Calculate result count with the new search term
      const term_lower = term.toLowerCase();
      const resultCount = users.filter(
        (user) =>
          user.email.toLowerCase().includes(term_lower) ||
          (user.name && user.name.toLowerCase().includes(term_lower))
      ).length;

      trackUserSearch(term, {
        resultCount,
        adminEmail: currentUser.email
      });
    }
  };

  /**
   * Handle sort field change
   */
  const handleSort = (field) => {
    const newDirection = sortField === field
      ? (sortDirection === "asc" ? "desc" : "asc")
      : "asc";

    setSortField(field);
    setSortDirection(newDirection);

    // Track sort change
    trackUserSortChange(field, newDirection, {
      adminEmail: currentUser.email
    });
  };

  /**
   * Handle user selection for bulk operations
   */
  const handleUserSelection = (userEmail, selected) => {
    if (selected) {
      setSelectedUsers([...selectedUsers, userEmail]);
    } else {
      setSelectedUsers(selectedUsers.filter((email) => email !== userEmail));
    }
  };

  /**
   * Handle select all users
   */
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedUsers(filteredUsers.map((user) => user.email));
    } else {
      setSelectedUsers([]);
    }
  };

  /**
   * Handle delete user confirmation
   */
  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteDialog(true);
  };

  /**
   * Confirm user deletion
   */
  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      const response = await fetch(
        `/api/v1/config/users/${encodeURIComponent(userToDelete.email)}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to delete user: ${response.status} ${response.statusText}`
        );
      }

      // Track successful deletion
      trackUserDelete(userToDelete.email, {
        adminEmail: currentUser.email,
        success: true,
        bulkDelete: false
      });

      onUserDeleted(userToDelete.email);
    } catch (error) {
      console.error("Error deleting user:", error);

      // Track failed deletion
      trackUserDelete(userToDelete.email, {
        adminEmail: currentUser.email,
        success: false,
        bulkDelete: false,
        error: error.message
      });

      alert(`Failed to delete user: ${error.message}`);
    } finally {
      setShowDeleteDialog(false);
      setUserToDelete(null);
    }
  };

  /**
   * Handle bulk delete selected users
   */
  const handleDeleteSelected = () => {
    if (selectedUsers.length === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  /**
   * Delete all selected users
   */
  const deleteSelectedUsers = async () => {
    setShowBulkDeleteConfirm(false);

    const successfulDeletions = [];
    const failedDeletions = [];

    for (const userEmail of selectedUsers) {
      try {
        const response = await fetch(
          `/api/v1/config/users/${encodeURIComponent(userEmail)}`,
          {
            method: "DELETE",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Failed to delete ${userEmail}: ${response.status} ${response.statusText}`
          );
        }

        successfulDeletions.push(userEmail);
        // Notify parent component of successful deletion
        onUserDeleted(userEmail);
      } catch (error) {
        console.error(`Error deleting user ${userEmail}:`, error);
        failedDeletions.push(userEmail);
      }
    }

    // Track bulk deletion with results
    if (successfulDeletions.length > 0) {
      trackUserDelete(successfulDeletions, {
        adminEmail: currentUser.email,
        success: true,
        bulkDelete: true,
        count: successfulDeletions.length
      });
    }

    if (failedDeletions.length > 0) {
      trackUserDelete(failedDeletions, {
        adminEmail: currentUser.email,
        success: false,
        bulkDelete: true,
        count: failedDeletions.length,
        error: `${failedDeletions.length} deletions failed`
      });
    }

    // Clear selection
    setSelectedUsers([]);

    // Show summary of results
    if (failedDeletions.length > 0) {
      console.error('Failed deletions:', failedDeletions);
      // Note: This should trigger an error message in UI
    } else {
      console.log(`Successfully deleted ${successfulDeletions.length} users`);
    }
  };

  // Note: getPrivilegeBadges and getFeatureBadges moved to UserListItem component

  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort className={styles.sortIcon} />;
    return <span className={styles.sortIconActive}>{sortDirection === "asc" ? "↑" : "↓"}</span>;
  };

  return (
    <div className={styles.container}>
      {/* Search and Filter Controls */}
      <div className={styles.controlsContainer}>
        <div className={styles.searchGroup}>
          <div className={styles.inputGroup}>
            <div className={styles.searchIcon}>
              <FaSearch />
            </div>
            <input
              className={styles.searchInput}
              type="text"
              placeholder="Search users by email or name..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
        </div>
        <div className={styles.infoGroup}>
          <span className={styles.countText}>
            Showing {filteredUsers.length} of {users.length} users
          </span>
          {selectedUsers.length > 0 && (
            <button
              className={styles.deleteSelectedButton}
              onClick={handleDeleteSelected}
            >
              Delete Selected ({selectedUsers.length})
            </button>
          )}
        </div>
      </div>

      {/* User Table (Desktop) */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.checkboxCell}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={
                    selectedUsers.length === filteredUsers.length &&
                    filteredUsers.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </th>
              <th onClick={() => handleSort("email")}>
                Email {getSortIcon("email")}
              </th>
              <th onClick={() => handleSort("name")}>
                Name {getSortIcon("name")}
              </th>
              <th onClick={() => handleSort("privileges")}>
                Privileges {getSortIcon("privileges")}
              </th>
              <th onClick={() => handleSort("features")}>
                Features {getSortIcon("features")}
              </th>
              <th className={styles.actionsCell}></th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <UserListItem
                key={user.email}
                user={user}
                selected={selectedUsers.includes(user.email)}
                onSelect={handleUserSelection}
                onEdit={onUserUpdated}
                onDelete={handleDeleteUser}
                currentUser={currentUser}
                viewMode="table"
              />
            ))}
            {filteredUsers.length === 0 && (
              <tr className={styles.emptyRow}>
                <td colSpan="6">
                  {searchTerm
                    ? "No users found matching your search."
                    : "No users found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Card List (Mobile) */}
      <div className={styles.cardList}>
        {filteredUsers.map((user) => (
          <UserListItem
            key={user.email}
            user={user}
            selected={selectedUsers.includes(user.email)}
            onSelect={handleUserSelection}
            onEdit={onUserUpdated}
            onDelete={handleDeleteUser}
            currentUser={currentUser}
            viewMode="card"
          />
        ))}
        {filteredUsers.length === 0 && (
          <div className={styles.emptyState}>
            {searchTerm
              ? "No users found matching your search."
              : "No users found."}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        show={showDeleteDialog}
        title="Delete User"
        message={
          userToDelete ? (
            <>
              Are you sure you want to delete user{" "}
              <strong>{userToDelete.email}</strong>?
              <br />
              <small className="text-muted">
                This action cannot be undone.
              </small>
            </>
          ) : (
            ""
          )
        }
        onConfirm={confirmDeleteUser}
        onCancel={() => {
          setShowDeleteDialog(false);
          setUserToDelete(null);
        }}
        variant="danger"
      />

      {/* Bulk Delete Confirmation Dialog */}
      <ConfirmDialog
        show={showBulkDeleteConfirm}
        title="Delete Multiple Users?"
        message={`Are you sure you want to delete ${selectedUsers.length} selected users? This action cannot be undone.`}
        confirmLabel="Delete Users"
        cancelLabel="Cancel"
        onConfirm={deleteSelectedUsers}
        onCancel={() => setShowBulkDeleteConfirm(false)}
        variant="danger"
      />
    </div>
  );
};

UserList.propTypes = {
  users: PropTypes.arrayOf(PropTypes.object).isRequired,
  onUserUpdated: PropTypes.func.isRequired,
  onUserDeleted: PropTypes.func.isRequired,
  currentUser: PropTypes.shape({
    email: PropTypes.string.isRequired,
    privileges: PropTypes.object.isRequired
  }).isRequired
};

export default UserList;
